import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentsService, IOcbCUEnvironment, IOcbEnvironment, IOcbEnvironmentAccess, UserService } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, map, mergeMap, take, tap } from 'rxjs/operators';

@Component({
    selector: 'app-environments',
    templateUrl: './environments.component.html',
    styleUrls: ['./environments.component.scss'],
})
export class EnvironmentsComponent implements AfterViewInit {

    @ViewChild('environments')
    public envGrid: DxDataGridComponent;

    @ViewChild('environmentValidation')
    public envValidationGroup: DxValidationGroupComponent;

    // Main Data-Grid
    public dataSource: IOcbEnvironment[] = null;
    public savedDataSource: IOcbEnvironment[] = null;
    public showDisabledEnvironments: boolean = false;
    public environmentsAccess: IOcbEnvironmentAccess[] = null;

    // Popup Display, New, Edit & Copy
    public isDisplayPopupVisible: boolean = false;
    public isEditPopupVisible: boolean = false;
    public selectedEnvironment: IOcbEnvironment = null;
    public isNew: boolean = null;
    public isCopy: boolean = null;

    /**
     * Boolean to show or hide the loading panel
     */
    public isLoadPanelVisible: boolean = false;

    constructor(
        private router: Router,
        private userService: UserService,
        private environmentsService: EnvironmentsService,
        private translateService: TranslateService) { }

    public ngAfterViewInit() {
        this.userService.user.pipe(
            take(1),
            map(user => user.environments.filter(env => env.canManageEnvironmentSettings)),
        ).subscribe(envs => {
            this.environmentsAccess = envs;
            this.loadDataSource();
        });
    }

    /**
     * Set Recipients context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.edit'),
                this.translateService.get('_commons.enable'),
                this.translateService.get('_commons.disable'),
                this.translateService.get('_commons.delete'),
                this.translateService.get('_commons.copy'),
            ).subscribe(
                messages => {

                    e.items = [
                        { text: messages[0], icon: 'edit', onItemClick: (ev) => this.goToUpdateForm(ev, e.row.data) },
                        e.row.data.isDisabled ?
                            { text: messages[1], icon: 'check', onItemClick: (ev) => this.enableEnvironment(ev, e.row.data) } :
                            { text: messages[2], icon: 'clear', onItemClick: (ev) => this.disableEnvironment(ev, e.row.data) },
                        { text: messages[3], icon: 'trash', onItemClick: (ev) => this.deleteEnvironment(ev, e.row.data.id) },
                        { text: messages[4], icon: 'copy', onItemClick: (ev) => this.displayCopyPopup(ev, e.row.data) },
                    ];
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Display update popup
     * @param e Handle event
     */
    public onRowDblClick(e) {
        if (e && e.data) {
            this.goToUpdateForm(e, e.data);
        }
    }

    /**
     * Configure rows
     * @param e Row object
     */
    public onRowPrepared(e): void {
        if (this.showDisabledEnvironments === true) {
            if (e.data != null) {
                if (e.data.isDisabled === true) { e.rowElement.bgColor = '#FFCECE'; }
            }
        }
    }

    /**
     * Dipslay new popup
     */
    public displayNewPopup(): void {
        this.selectedEnvironment = this.getNewIOcbEnvironment();
        this.isNew = true;
        this.isCopy = false;
        this.isEditPopupVisible = true;
    }

    /**
     * Display update popup
     * @param e Handle event
     * @param environment Environment data to update
     */
    public goToUpdateForm(e, environment: IOcbEnvironment): void {
        if (e && e.event) {
            e.event.stopPropagation();
        }
        this.router.navigate([environment.id, 'profile']);
    }

    /**
     * Display copy popup
     * @param e Handle event
     * @param environment Environment data to copy
     */
    public displayCopyPopup(e, environment: IOcbEnvironment): void {
        e.event.stopPropagation();
        this.selectedEnvironment = this.getIOcbEnvironmentFromData(environment);
        this.selectedEnvironment.name = '';
        this.isNew = false;
        this.isCopy = true;
        this.isEditPopupVisible = true;
    }

    /**
     * Create an environment
     */
    public createEnvironment(): void {
        if (this.envValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newEnvironment = this.getIOcbCUEnvironmentFromData(this.selectedEnvironment);
            this.environmentsService.addEnvironment(newEnvironment)
                .pipe(
                    mergeMap(_ => this.translateService.get('environments.msgEnvironmentCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(message => {
                    this.isEditPopupVisible = false;
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
        }
    }

    /**
     * Delete an environment
     * @param e Handle event
     * @param id Internal identifier of the environment
     */
    public deleteEnvironment(e, id): void {
        e.event.stopPropagation();
        forkJoin(
            // Get translation messages
            this.translateService.get('environments.msgDeleteConfirm'),
            this.translateService.get('environments.msgDeleteConfirmTitle'))
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.environmentsService.deleteEnvironment(id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('environments.msgEnvironmentDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe(
                message => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
    }

    /**
     * Copy an environment
     */
    public copyEnvironment() {
        if (this.envValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            this.environmentsService.addEnvironment(this.selectedEnvironment)
                .pipe(
                    mergeMap(addedEnvironment => {
                        this.dataSource.push(addedEnvironment);
                        return this.translateService.get('environments.msgEnvironmentCopyComplete');
                    }),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(
                    message => {
                        this.isEditPopupVisible = false;
                        notify(message, 'success', 3000);
                    }, err => this.handleError(err));
        }
    }

    /**
     * Enable an environment
     * @param e Handle event
     * @param environment Environment to enable
     */
    public enableEnvironment(e, environment) {
        e.event.stopPropagation();
        environment.isDisabled = false;
        this.environmentsService.updateEnvironment(environment.id, environment).subscribe(res => {
            this.translateService.get('environments.msgEnvironmentEnableComplete').subscribe(message => {
                notify(message, 'success', 1000);
            });
            this.envGrid.instance.refresh();
        },
            err => this.handleError(err));
        if (this.dataSource.length === 0) {
            this.dataSource = [];
        }
    }

    /**
     * Disable an environment
     * @param e Handle event
     * @param environment Environment to disable
     */
    public disableEnvironment(e, environment) {
        e.event.stopPropagation();
        environment.isDisabled = true;
        this.environmentsService.updateEnvironment(environment.id, environment).subscribe(res => {
            this.translateService.get('environments.msgEnvironmentDisableComplete').subscribe(message => {
                notify(message, 'success', 1000);
            });
            this.envGrid.instance.refresh();
            if (this.showDisabledEnvironments === false) {
                this.dataSource = this.savedDataSource.filter(a => !a.isDisabled);
            }
        },
            err => this.handleError(err));
        if (this.dataSource.length === 0) {
            this.dataSource = [];
        }
    }

    /**
     * Change value of the boolean to filter disabled environments or not and reload data source
     * @param e Boolean to show disabled environments or not
     */
    public filterChanged(e) {
        if (e) {
            this.showDisabledEnvironments = true;
            this.dataSource = this.savedDataSource;
        } else {
            this.showDisabledEnvironments = false;
            this.dataSource = this.savedDataSource.filter(a => !a.isDisabled);
        }
    }

    /**
     * Load data source with environments
     */
    private loadDataSource(): void {
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.environmentsService.getEnvironments(true)),
                finalize(() => this.envGrid.instance.endCustomLoading()))
            .subscribe(
                res => {
                    this.dataSource = res;
                    this.savedDataSource = this.dataSource;
                    if (!this.showDisabledEnvironments) {
                        this.dataSource = this.savedDataSource.filter(a => !a.isDisabled);
                    }
                },
                err => this.handleError(err));
    }

    /**
     * Get a new and empty environment object
     */
    private getNewIOcbEnvironment(): IOcbEnvironment {
        return {
            id: '',
            name: '',
            unknownFilesFolder: null,
            isDisabled: false,
            countAttemptsBeforeNotification: 5,
            logsRetention: null,
            maxDelayToReceiveACK: 55,
            globalTimer: 900,
            laDelayBeforeStopping: 60,
            laIntervalCheckCommandQueue: 10,
            lamIntervalCheckCommandQueue: 10
        };
    }

    /**
     * Get an environment object from another one
     */
    private getIOcbEnvironmentFromData(data: IOcbEnvironment): IOcbEnvironment {
        return {
            id: data.id,
            name: data.name,
            globalTimer: data.globalTimer,
            maxDelayToReceiveACK: data.maxDelayToReceiveACK,
            unknownFilesFolder: data.unknownFilesFolder,
            logsRetention: data.logsRetention,
            lamIntervalCheckCommandQueue: data.lamIntervalCheckCommandQueue,
            laIntervalCheckCommandQueue: data.laIntervalCheckCommandQueue,
            laDelayBeforeStopping: data.laDelayBeforeStopping,
            countAttemptsBeforeNotification: data.countAttemptsBeforeNotification,
            isDisabled: data.isDisabled,
        };
    }

    /**
     * Get an environment object from another one
     */
    private getIOcbCUEnvironmentFromData(data: IOcbEnvironment): IOcbCUEnvironment {
        return {
            name: data.name,
            globalTimer: data.globalTimer,
            maxDelayToReceiveACK: data.maxDelayToReceiveACK,
            unknownFilesFolder: data.unknownFilesFolder,
            logsRetention: data.logsRetention,
            lamIntervalCheckCommandQueue: data.lamIntervalCheckCommandQueue,
            laIntervalCheckCommandQueue: data.laIntervalCheckCommandQueue,
            laDelayBeforeStopping: data.laDelayBeforeStopping,
            countAttemptsBeforeNotification: data.countAttemptsBeforeNotification,
            isDisabled: data.isDisabled,
        };
    }

    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.envGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
